import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tenantsApi, ApiError } from '../api/client';

const COMMON_TIMEZONES = [
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Hong_Kong',
  'Asia/Dubai',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
  'UTC',
];

export function TenantCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    slug: '',
    display_name: '',
    timezone: 'Asia/Bangkok',
    custom_domain: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const created = await tenantsApi.create({
        slug: form.slug.trim().toLowerCase(),
        display_name: form.display_name.trim(),
        timezone: form.timezone,
        custom_domain: form.custom_domain.trim() || null,
      });
      navigate(`/tenants/${created.tenant.id}`);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.body.message ?? err.body.error ?? 'Failed to create tenant'
          : 'Failed to create tenant',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <Link to="/tenants" className="text-xs text-ink-muted hover:text-ink uppercase tracking-wider">
          ← Tenants
        </Link>
        <h1 className="font-heading text-2xl font-semibold tracking-tight mt-2">New tenant</h1>
        <p className="text-sm text-ink-muted">
          Creates the tenant record in PENDING state. Brand, offerings, email, and the actual
          D1 provisioning are configured after creation.
        </p>
      </div>

      <form onSubmit={onSubmit} className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <Field label="Display name" hint="Shown in dashboards and emails." required>
          <input
            type="text"
            required
            value={form.display_name}
            onChange={(e) => update('display_name', e.target.value)}
            placeholder="Live More Clinic"
            className="w-full rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink outline-none ring-1 ring-transparent focus:ring-primary"
          />
        </Field>

        <Field
          label="Slug"
          hint="Used in the tenant subdomain. Lowercase letters, digits, hyphens. 1–40 chars."
          required
        >
          <input
            type="text"
            required
            value={form.slug}
            onChange={(e) => update('slug', e.target.value.toLowerCase())}
            pattern="[a-z0-9](?:[a-z0-9\-]{0,38}[a-z0-9])?"
            placeholder="livemore"
            className="w-full rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink font-mono outline-none ring-1 ring-transparent focus:ring-primary"
          />
          <p className="text-xs text-ink-muted mt-1.5">
            Tenant URL: <span className="font-mono text-ink">{form.slug || '<slug>'}.auremhealth.dev</span>
          </p>
        </Field>

        <Field label="Timezone" hint="Used for email send times, deadlines, and timestamps." required>
          <select
            required
            value={form.timezone}
            onChange={(e) => update('timezone', e.target.value)}
            className="w-full rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink outline-none ring-1 ring-transparent focus:ring-primary"
          >
            {COMMON_TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </Field>

        <Field
          label="Custom domain (optional)"
          hint="If the tenant wants a white-label hostname. Set up later via Cloudflare Custom Domains; can be left empty for now."
        >
          <input
            type="text"
            value={form.custom_domain}
            onChange={(e) => update('custom_domain', e.target.value)}
            placeholder="portal.livemore.health"
            className="w-full rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink font-mono outline-none ring-1 ring-transparent focus:ring-primary"
          />
        </Field>

        {error && <p role="alert" className="text-sm text-bad">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting || !form.slug || !form.display_name}
            className="rounded-lg bg-primary px-4 py-2.5 font-medium text-ink transition disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create tenant'}
          </button>
          <Link
            to="/tenants"
            className="rounded-lg bg-surface-3 px-4 py-2.5 font-medium text-ink-muted hover:text-ink transition"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({
  label, hint, required, children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-ink-muted mb-1.5">
        {label}{required ? ' *' : ''}
      </span>
      {children}
      {hint && <p className="text-xs text-ink-muted mt-1.5">{hint}</p>}
    </label>
  );
}
